import { ExclamationCircleFilled, WarningFilled } from '@ant-design/icons'
import transportIcon from '@assets/CustomIcons/order_flags/transportation_flag.svg'
import { PopoverContent } from '@components/_SiteWide/Flags/PopoverContent'
import { Texto, GraviButton, BBDTag, Horizontal, useDeviceType } from '@gravitate-js/excalibrr'
import { Avatar, Badge, Col, Popover, Row, Tooltip } from 'antd'
import React, { useMemo, useState } from 'react'

import { AnalyticIcon } from '@components/StoreMonitoring/StoreDashboard/TankAlertDisplay/components/AnalyticIcon'
import { useSecurityContext } from '../../Navigation/SecurityContext'
import { FlagIconSwitch2 } from './FlagIconSwitch'
import { StatusAvatar } from './StatusAvatar'

function FlagAvatarGroup({ flags, isOrderFlag = false, view = null }) {
  const visibleFlags = flags.slice(0, 2)
  return (
    <Avatar.Group max={{ count: 2, offset: [10, 10] }}>
      {visibleFlags.map((flag, index) => (
        <Tooltip key={index} title={flag.name}>
          <Avatar
            size='small'
            style={{
              cursor: 'pointer',
              backgroundColor: 'var(--bg-1)',
            }}
            icon={<SingleFlag flag={flag} small isOrderFlag={isOrderFlag} view={view} />}
          />
        </Tooltip>
      ))}
    </Avatar.Group>
  )
}

export function FlagIcon({
  flags,
  hideRunout = true,
  orderNumber = '',
  orderId = '',
  updateOrder = () => {},
  filterResolvedFlags = true,
  hideOrderNumber = false,
  isOrderFlag = false,
  view = null,
  allowFlagResolve = true,
  useButtonStyle = false,
  allowCarrierVisibility = false,
}) {
  const [isVisible, setIsVisible] = useState(false)
  const { scopes } = useSecurityContext()
  const isCarrier = scopes?.is_carrier
  if (isCarrier && !allowCarrierVisibility) return null
  const { isMobile } = useDeviceType()

  if (hideRunout) {
    flags = flags.filter((f) => f.type !== 'run_out' && f.type !== 'unset' && f.type !== 'tank_reading')
  }
  if (filterResolvedFlags) {
    flags = flags.filter((flag) => flag.reason_code === null)
  }

  if (flags.length === 0) return null
  const popoverContent = (flags) => {
    return (
      <div>
        {flags.map((f, i) => {
          return (
            <PopoverContent
              key={i}
              index={i}
              flag={f}
              isLast={i === flags.length - 1}
              orderNumber={orderNumber}
              orderId={orderId}
              setIsVisible={setIsVisible}
              updateOrder={updateOrder}
              isVisible={isVisible}
              isOrderFlag={isOrderFlag}
              allowFlagResolve={allowFlagResolve}
            />
          )
        })}
      </div>
    )
  }
  const toggleVisibility = (open, buttonClose = false) => {
    if (buttonClose) setIsVisible(false)
    open ? setIsVisible(true) : setIsVisible(false)
  }
  if (flags.length > 1)
    return (
      <Popover
        trigger={['contextMenu', 'click']}
        placement={isMobile ? 'top' : 'topLeft'}
        content={popoverContent(flags)}
        onOpenChange={toggleVisibility}
        open={isVisible}
        title={
          <>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <Texto style={{ flex: 1 }}> Order Flags </Texto>
              {!hideOrderNumber && <Texto> Order {orderNumber} </Texto>}
            </div>
          </>
        }
      >
        <Tooltip title='View Flag Details'>
          <div className='vertical-flex-center items-center' style={{ cursor: 'pointer' }}>
            <FlagAvatarGroup flags={flags} isOrderFlag={isOrderFlag} view={view} />
          </div>
        </Tooltip>
      </Popover>
    )
  return (
    <Popover
      trigger={['click']}
      placement={isMobile ? 'top' : 'topLeft'}
      content={popoverContent(flags)}
      onOpenChange={toggleVisibility}
      open={isVisible}
      title={
        <>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <Texto style={{ flex: 1 }}> Order Flags </Texto>
            {!hideOrderNumber && <Texto> Order {orderNumber} </Texto>}
          </div>
        </>
      }
    >
      {useButtonStyle ? (
        <Tooltip title='View Flag Details'>
          <div className='vertical-flex-center items-center'>
            <FlagButton flag={flags[0]} isOrderFlag={isOrderFlag} />
          </div>
        </Tooltip>
      ) : (
        <Tooltip title='View Flag Details'>
          <div className='vertical-flex-center items-center' style={{ cursor: 'pointer' }}>
            <SingleFlag flag={flags[0]} small isOrderFlag={isOrderFlag} view={view} />
          </div>
        </Tooltip>
      )}
    </Popover>
  )
}
export function TransportationOrderFlag() {
  return (
    <Tooltip title='Transportation Order'>
      <div className='vertical-flex-center items-center mr-2'>
        <img className='danger-icon small' src={transportIcon} />
      </div>
    </Tooltip>
  )
}
function FlagButton({ flag, isOrderFlag, view }) {
  if (flag?.severity === 'money') {
    return (
      <GraviButton
        size='small'
        color='success'
        style={{ fontSize: '1.2em', width: 30, backgroundColor: 'var(--theme-success)' }}
      >
        <FlagIconSwitch2 flag={flag} view={view} isOrderFlag={isOrderFlag} />
      </GraviButton>
    )
  }
  if (flag?.severity === 'warning') {
    return (
      <GraviButton
        size='small'
        color='warning'
        style={{ fontSize: '1.2em', width: 30, backgroundColor: 'var(--theme-warning)' }}
      >
        <FlagIconSwitch2 flag={flag} view={view} isOrderFlag={isOrderFlag} />
      </GraviButton>
    )
  }
  return (
    <GraviButton style={{ fontSize: '1.2em', width: 30 }} error={flag?.severity === 'error'} size='small'>
      <FlagIconSwitch2 flag={flag} view={view} isOrderFlag={isOrderFlag} />
    </GraviButton>
  )
}
function MultipleFlag({ flags, small = false }) {
  const smallClass = small ? 'small' : ''
  if (flags.find((f) => f.severity === 'error'))
    return <ExclamationCircleFilled className={`danger-icon ${smallClass}`} />
  if (flags.find((f) => f.severity === 'warning')) return <WarningFilled className={`warning-icon ${smallClass}`} />
  if (flags.find((f) => f.severity === 'money'))
    return <WarningFilled style={{ color: 'var(--theme-success)' }} className={`warning-icon ${smallClass}`} />
  return <WarningFilled style={{ color: 'var(--theme-option)' }} className={`warning-icon ${smallClass}`} />
}
export function SingleFlag({ flag, small = false, isOrderFlag = false, view }) {
  const icon = <AnalyticIcon title={flag.name} size={small ? 'small' : 'medium'} />
  if (icon.ref !== null) return icon
  const smallClass = small ? 'small' : ''
  return (
    <StatusAvatar
      severity={flag.severity}
      icon={<FlagIconSwitch2 flag={flag} view={view} isOrderFlag={isOrderFlag} />}
      type={flag.type}
    />
  )
}

export const getFlagsStatus = (item) => {
  if (item.flags && item.flags.length > 0) {
    if (item.flags.find((f) => f.severity === 'error')) return 'error'
    if (item.flags.find((f) => f.severity === 'warning')) return 'warning'
    if (item.flags.find((f) => f.severity === 'money')) return 'success'
  }
  return ''
}

export function OrderFlagIcon({ row, updateOrder, filterResolvedFlags = true, allowFlagResolve = true }) {
  if (row.type === 'Backhaul') return <TransportationOrderFlag />
  const showOrderFlags = useMemo(() => row.unresolved_flags && row.unresolved_flags.length > 0, [row.unresolved_flags])
  const dependentFlags = useMemo(
    () => row.unresolved_flags?.filter((f) => f.type === 'dependent'),
    [row.unresolved_flags]
  )
  const otherFlags = useMemo(() => row.unresolved_flags?.filter((f) => f.type !== 'dependent'), [row.unresolved_flags])

  if (showOrderFlags) {
    return (
      <Row>
        <Col className='vertical-flex-center' span={10}>
          {otherFlags.length > 0 && (
            <FlagIcon
              flags={otherFlags}
              orderNumber={row.number}
              orderId={row.id}
              updateOrder={updateOrder}
              isOrderFlag
              filterResolvedFlags={filterResolvedFlags}
              allowFlagResolve={allowFlagResolve}
            />
          )}
        </Col>
        <Col className='vertical-flex-center' span={10}>
          {dependentFlags.length > 0 && (
            <FlagIcon
              flags={dependentFlags}
              orderNumber={row.number}
              orderId={row.id}
              updateOrder={updateOrder}
              filterResolvedFlags={filterResolvedFlags}
              allowFlagResolve={allowFlagResolve}
            />
          )}
        </Col>
      </Row>
    )
  }
  return null
}
export function OrderPriceFlagIcon({ row, updateOrder }) {
  if (row.type?.toLowerCase() === 'backhaul') return null
  const showOrderFlags = row.price_flag
  if (showOrderFlags) {
    const priceFlag = [row.price_flag]
    return (
      <Row>
        <Col className='verical-flex-center' span={12}>
          <FlagIcon
            flags={priceFlag}
            filterResolvedFlags={false}
            orderId={row.id}
            orderNumber={row.number}
            updateOrder={updateOrder}
          />
        </Col>
      </Row>
    )
  }
  return null
}
